import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import "./Login.css";
import userIcon from "../assets/icons/login-user.png";
import { API_BASE } from "../utils/apiBase";

export default function Login() {
  const navigate = useNavigate();
  const { login, setIdleTimeoutMs } = useAuth();

  const [uid, setUid] = useState("");
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  /* ================= ENTER KEY HANDLING (FROM DEVELOP) ================= */
  useEffect(() => {
    const onEnter = (e) => {
      if (e.key === "Enter") handleLogin(e);
    };
    window.addEventListener("keydown", onEnter);
    return () => window.removeEventListener("keydown", onEnter);
  }, [uid, pwd, loading]);

  /* ================= LOGOUT MESSAGE ================= */
  useEffect(() => {
    const reason = localStorage.getItem("logout_reason");
    if (reason) {
      setInfoMessage(reason);
      localStorage.removeItem("logout_reason");
    }
  }, []);

  /* ================= LOAD IDLE TIMEOUT ================= */
  const loadIdleTimeout = async (token) => {
    try {
      const res = await fetch("/api/settings/idle-timeout", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error();

      const data = await res.json();
      setIdleTimeoutMs(data.idleLogoutMinutes * 60 * 1000);
    } catch {
      // safe fallback → 5 minutes
      setIdleTimeoutMs(5 * 60 * 1000);
    }
  };

  /* ================= LOGIN HANDLER ================= */
  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError("");
    setLoading(true);

    try {
      /* ===== NORMAL LOGIN ===== */
      //console.log("VITE_API_URL =", import.meta.env.VITE_API_URL);

      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, pwd }),
      });

      const data = await res.json();

      /* 🔴 SESSION EXISTS FLOW */
      if (data.code === "SESSION_EXISTS") {
        setLoading(false);

        const proceed = window.confirm(
          data.message ||
            "You are already logged in. Would you like to close all other sessions and login now?",
        );

        if (!proceed) return;

        const forceRes = await fetch("/api/login/force", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uid, pwd }),
        });

        const forceData = await forceRes.json();

        if (!forceRes.ok) {
          setError(forceData.message || "Unable to login");
          return;
        }

        login({
          token: forceData.token,
          uid,
          usertype: forceData.usertype,
        });

        await loadIdleTimeout(forceData.token);
        navigate("/", { replace: true });
        return;
      }

      /* ❌ NORMAL LOGIN FAILURE */
      if (!res.ok) {
        setError(data.message || "Invalid credentials");
        return;
      }

      /* ✅ NORMAL LOGIN SUCCESS */
      login({
        token: data.token,
        uid,
        usertype: data.usertype,
      });

      await loadIdleTimeout(data.token);
      navigate("/", { replace: true });

    } catch {
      setError("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI (UNCHANGED FROM DEVELOP) ================= */
  return (
    <div className="login-wrapper">
      <div className="login-card">
        <img src={userIcon} className="login-icon" alt="User Login" />
        <h2 className="login-title">User Login</h2>

        {infoMessage && <div className="login-info">{infoMessage}</div>}

        <form
          onSubmit={handleLogin}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
        >
          <div className="form-group">
            <input
              type="text"
              name="uid_fake"
              placeholder="User ID"
              value={uid}
              onChange={(e) => setUid(e.target.value)}
              required
              disabled={loading}
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <input
              type="password"
              name="pwd_fake"
              placeholder="Password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              required
              disabled={loading}
              autoComplete="new-password"
            />
          </div>

          {error && <div className="login-error">{error}</div>}

          <button className="btn-primary login-btn" disabled={loading}>
            {loading ? "Signing in..." : "Login"}
          </button>

          <div className="login-links">
            <a href="/forgot-password">Forgot Password?</a>
          </div>
        </form>
      </div>
    </div>
  );
}
